import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useSegmentFields } from '../hooks/QueryBuilder/useSegmentFields';
import { useAvailableGroups, AvailableGroup } from '../hooks/QueryBuilder/useAvailableGroups';
import { QueryField } from '../types/QueryBuilder';

// Re-export the type for consumers
export type { AvailableGroup };

interface QueryBuilderDataContextType {
    // Segment Fields
    fields: QueryField[];
    loadingFields: boolean;
    errorFields: string | null;
    categories: string[];
    simpleFields: QueryField[];
    getFieldsByCategory: (category: string) => QueryField[];
    refetchFields: () => void;
    
    // Available Groups
    groups: AvailableGroup[];
    loadingGroups: boolean;
    errorGroups: string | null;
    refetchGroups: () => void;
    
    // Global loading state
    loading: boolean;
    error: string | null;
}

const QueryBuilderDataContext = createContext<QueryBuilderDataContextType | undefined>(undefined);

export const QueryBuilderDataProvider = ({ children }: { children: ReactNode }) => {
    const {
        fields,
        loading: loadingFields,
        error: errorFields,
        categories,
        simpleFields,
        getFieldsByCategory,
        refetch: refetchFields
    } = useSegmentFields();
    
    const { 
        groups, 
        loading: loadingGroups, 
        error: errorGroups, 
        refetch: refetchGroups 
    } = useAvailableGroups();

    const value: QueryBuilderDataContextType = useMemo(() => ({
        // Segment Fields
        fields,
        loadingFields,
        errorFields,
        categories,
        simpleFields,
        getFieldsByCategory,
        refetchFields,

        // Available Groups
        groups,
        loadingGroups,
        errorGroups,
        refetchGroups,

        // Global state
        loading: loadingFields || loadingGroups,
        error: errorFields || errorGroups || null
    }), [
        fields,
        loadingFields,
        errorFields,
        categories,
        simpleFields,
        getFieldsByCategory,
        refetchFields,
        groups,
        loadingGroups,
        errorGroups,
        refetchGroups
    ]);

    return (
        <QueryBuilderDataContext.Provider value={value}>
            {children}
        </QueryBuilderDataContext.Provider>
    );
};

export const useQueryBuilderData = (): QueryBuilderDataContextType => {
    const context = useContext(QueryBuilderDataContext);
    if (context === undefined) {
        throw new Error('useQueryBuilderData must be used within a QueryBuilderDataProvider');
    }
    return context;
};
